/**
 * Page de détails d'une exécution
 *
 * Fonctionnalités:
 * - Détails complets de l'exécution
 * - Live logs streaming
 * - Plan output
 * - Terraform state
 * - Artefacts
 * - Actions (cancel, retry, approve)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../Dashboard/DashboardLayout';
import { useToast } from '../../context/ToastContext';
import {
  getExecution,
  streamExecutionLogs,
  pollExecutionStatus,
  getPlanOutput,
  getTerraformState,
  getExecutionArtifacts,
  getExecutionSteps,
  getExecutionLogs,
  getStepStatistics,
  cancelExecution,
  retryExecution,
  approveExecution,
  rejectExecution,
  type Execution,
  type ExecutionLog,
  type ExecutionArtifact,
  type ExecutionStep,
  type StepStatistics
} from '../../api/executions';
import StepCard from './StepCard';
import ConfirmationModal from '../common/ConfirmationModal';
import CodeBlock from '../common/CodeBlock';
import './ExecutionDetailsPage.css';

const ExecutionDetailsPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [execution, setExecution] = useState<Execution | null>(null);
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [planOutput, setPlanOutput] = useState<any>(null);
  const [terraformState, setTerraformState] = useState<any>(null);
  const [artifacts, setArtifacts] = useState<ExecutionArtifact[]>([]);
  const [steps, setSteps] = useState<ExecutionStep[]>([]);
  const [stepStatistics, setStepStatistics] = useState<StepStatistics>({});
  const [selectedStep, setSelectedStep] = useState<ExecutionStep | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'logs' | 'plan' | 'state' | 'artifacts'>('logs');
  const [autoScroll, setAutoScroll] = useState(true);
  const [elapsedTime, setElapsedTime] = useState<number>(0); // Temps écoulé en secondes

  // Logs filtering and search
  const [logLevelFilter, setLogLevelFilter] = useState<string>('all');
  const [logSearchQuery, setLogSearchQuery] = useState<string>('');

  // Confirmation modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const logsEndRef = useRef<HTMLDivElement>(null);
  const logStreamRef = useRef<{ stop: () => void } | null>(null);
  const statusPollRef = useRef<{ stop: () => void } | null>(null);
  const userSelectedStepRef = useRef<boolean>(false); // Track if user manually selected a step
  const notificationShownRef = useRef<boolean>(false); // Track if notification was already shown

  /**
   * Scroll vers le bas des logs
   */
  const scrollToBottom = () => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /**
   * Charger les détails de l'exécution
   */
  const loadExecution = async () => {
    if (!runId) return;

    try {
      setError(null);
      const data = await getExecution(runId);
      setExecution(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load execution');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Démarrer le streaming des logs
   */
  const startLogStreaming = () => {
    if (!runId || logStreamRef.current) return;

    logStreamRef.current = streamExecutionLogs(
      runId,
      (log) => {
        setLogs((prev) => [...prev, log]);
      },
      (error) => {
        console.error('Log streaming error:', error);
      },
      2000 // Poll every 2 seconds
    );
  };

  /**
   * Démarrer le polling du statut
   */
  const startStatusPolling = () => {
    if (!runId || statusPollRef.current) return;

    pollExecutionStatus(
      runId,
      (exec) => {
        setExecution(exec);
      },
      async (exec) => {
        setExecution(exec);

        // Do a final poll of logs before stopping the stream
        if (logStreamRef.current) {
          // Wait a bit for logs to be saved
          await new Promise(resolve => setTimeout(resolve, 1500));

          // Force one last poll to get all logs
          try {
            const finalLogs = await getExecutionLogs(runId);
            console.log('Final logs poll: got', finalLogs.length, 'logs');
            if (finalLogs.length > 0) {
              setLogs(finalLogs);
            }
          } catch (error) {
            console.error('Error in final logs poll:', error);
          }

          // Now stop the streaming (check again in case it was cleaned up)
          if (logStreamRef.current) {
            logStreamRef.current.stop();
            logStreamRef.current = null;
          }
        }
      },
      (error) => {
        console.error('Status polling error:', error);
      },
      3000, // Poll every 3 seconds
      600000 // Timeout after 10 minutes
    ).then((result) => {
      statusPollRef.current = result;
    });
  };

  /**
   * Charger le plan output
   */
  const loadPlanOutput = async () => {
    if (!runId) return;

    try {
      const data = await getPlanOutput(runId);
      setPlanOutput(data);
    } catch (err: any) {
      // Plan output may not exist yet
      console.error('Failed to load plan output:', err);
    }
  };

  /**
   * Charger le state Terraform
   */
  const loadTerraformState = async () => {
    if (!runId) return;

    try {
      const data = await getTerraformState(runId);
      setTerraformState(data);
    } catch (err: any) {
      // State may not exist yet
      console.error('Failed to load terraform state:', err);
    }
  };

  /**
   * Charger les artefacts
   */
  const loadArtifacts = async () => {
    if (!runId) return;

    try {
      const data = await getExecutionArtifacts(runId);
      setArtifacts(data);
    } catch (err: any) {
      console.error('Failed to load artifacts:', err);
    }
  };

  /**
   * Charger les étapes de la timeline
   */
  const loadSteps = async () => {
    if (!runId) return;

    try {
      const data = await getExecutionSteps(runId);
      setSteps(data);

      // Auto-sélectionner seulement si l'utilisateur n'a pas encore fait de sélection manuelle
      if (!selectedStep && !userSelectedStepRef.current) {
        const runningStep = data.find(s => s.status === 'running');
        const lastStep = data[data.length - 1];
        setSelectedStep(runningStep || lastStep || null);
      } else if (selectedStep) {
        // Mettre à jour l'étape sélectionnée avec les nouvelles données
        const updatedSelectedStep = data.find(s => s.id === selectedStep.id);
        if (updatedSelectedStep) {
          setSelectedStep(updatedSelectedStep);
        }
      }
    } catch (err: any) {
      console.error('Failed to load execution steps:', err);
    }
  };

  /**
   * Charger les statistiques de durées moyennes par step
   */
  const loadStepStatistics = async () => {
    if (!execution?.architecture_id || !execution?.action) return;

    try {
      const stats = await getStepStatistics(execution.architecture_id, execution.action, 10);
      setStepStatistics(stats);
      console.log('📊 Step statistics loaded:', stats);
    } catch (err: any) {
      console.error('Failed to load step statistics:', err);
      // Ne pas bloquer l'exécution si les stats ne sont pas disponibles
    }
  };

  /**
   * Démarrer le polling des steps (refresh toutes les 2 secondes)
   */
  const startStepsPolling = () => {
    if (!runId) return;

    // Charger immédiatement
    loadSteps();

    // Puis charger toutes les 2 secondes
    const interval = setInterval(() => {
      loadSteps();

      // Arrêter le polling si l'exécution est terminée
      if (execution && ['success', 'succeeded', 'failed', 'cancelled'].includes(execution.status || '')) {
        clearInterval(interval);
        console.log('Steps polling stopped - execution completed');
      }
    }, 2000);

    return () => clearInterval(interval);
  };

  /**
   * Effect: Charger au montage
   */
  useEffect(() => {
    loadExecution();
    startLogStreaming();
    startStatusPolling();
    loadArtifacts();
    const stopStepsPolling = startStepsPolling();
    loadStepStatistics(); // Charger les statistiques

    return () => {
      if (logStreamRef.current) {
        logStreamRef.current.stop();
      }
      if (statusPollRef.current) {
        statusPollRef.current.stop();
      }
      if (stopStepsPolling) {
        stopStepsPolling();
      }
    };
  }, [runId]);

  /**
   * Effect: Auto-scroll des logs
   */
  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  /**
   * Effect: Charger les logs quand l'exécution se termine
   */
  useEffect(() => {
    const loadLogsOnComplete = async () => {
      if (!execution || !runId) return;

      // Vérifier si l'exécution est terminée
      const isCompleted = ['success', 'succeeded', 'failed', 'cancelled'].includes(execution.status || '');

      if (isCompleted && logs.length === 0) {
        console.log('[AUTO-LOAD] Execution completed, loading logs...');

        // Attendre un peu pour laisser le backend sauvegarder les logs
        await new Promise(resolve => setTimeout(resolve, 2000));

        try {
          const executionLogs = await getExecutionLogs(runId);
          console.log('[AUTO-LOAD] Loaded', executionLogs.length, 'logs');
          if (executionLogs.length > 0) {
            setLogs(executionLogs);
          }
        } catch (error) {
          console.error('[AUTO-LOAD] Error loading logs:', error);
        }
      }
    };

    loadLogsOnComplete();
  }, [execution?.status, runId]);

  /**
   * Effect: Charger plan/state selon le tab actif
   */
  useEffect(() => {
    if (activeTab === 'plan' && !planOutput) {
      loadPlanOutput();
    } else if (activeTab === 'state' && !terraformState) {
      loadTerraformState();
    }
  }, [activeTab]);

  /**
   * Effect: Mettre à jour le temps écoulé toutes les secondes
   */
  useEffect(() => {
    if (!execution) return;

    // Calculer le temps écoulé initial
    const calculateElapsed = () => {
      const startTime = execution.started_at || execution.created_at;
      if (!startTime) return 0;

      // Forcer l'interprétation UTC (ajouter Z si absent pour éviter décalage timezone)
      const toUtc = (ts: string) => ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z';
      const start = new Date(toUtc(startTime)).getTime();
      const now = Date.now();
      const endTime = execution.completed_at ? new Date(toUtc(execution.completed_at)).getTime() : now;

      return Math.floor((endTime - start) / 1000);
    };

    // Mettre à jour immédiatement
    setElapsedTime(calculateElapsed());

    // Arrêter le timer si l'exécution est terminée
    if (['success', 'succeeded', 'failed', 'cancelled'].includes(execution.status || '')) {
      return;
    }

    // Mettre à jour toutes les secondes pour les exécutions en cours
    const interval = setInterval(() => {
      setElapsedTime(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [execution]);

  /**
   * Effect: Afficher une notification quand l'exécution se termine (UNE SEULE FOIS)
   */
  useEffect(() => {
    if (!execution || !runId) return;

    const status = execution.status;
    const isCompleted = ['success', 'succeeded', 'failed', 'cancelled'].includes(status || '');

    // Ne montrer la notification qu'une seule fois
    if (isCompleted && !notificationShownRef.current) {
      notificationShownRef.current = true;

      // Afficher la notification toast
      if (status === 'success' || status === 'succeeded') {
        toast.success(`✅ Execution ${execution.action?.toUpperCase()} completed successfully!`);

        // Notification navigateur
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Execution Completed', {
            body: `${execution.action?.toUpperCase()} execution completed successfully`,
            icon: '/favicon.ico',
            tag: runId
          });
        }
      } else if (status === 'failed') {
        toast.error(`❌ Execution ${execution.action?.toUpperCase()} failed`);

        // Notification navigateur
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Execution Failed', {
            body: `${execution.action?.toUpperCase()} execution failed: ${execution.error_message || 'Unknown error'}`,
            icon: '/favicon.ico',
            tag: runId
          });
        }
      } else if (status === 'cancelled') {
        toast.warning(`⚠️ Execution ${execution.action?.toUpperCase()} was cancelled`);
      }
    }
  }, [execution?.status, runId, toast]);

  /**
   * Effect: Demander la permission pour les notifications navigateur au montage
   */
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          toast.info('🔔 Browser notifications enabled for executions');
        }
      });
    }
  }, []);

  /**
   * Annuler l'exécution
   */
  const handleCancel = () => {
    if (!runId) return;
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!runId) return;

    try {
      await cancelExecution(runId, 'Cancelled by user');
      await loadExecution();
      toast.success('✅ Execution cancelled successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to cancel execution');
    }
  };

  /**
   * Retry l'exécution
   */
  const handleRetry = () => {
    if (!runId) return;
    setShowRetryModal(true);
  };

  const confirmRetry = async () => {
    if (!runId) return;

    try {
      await retryExecution(runId);

      // Attendre que la base de données soit mise à jour
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Effacer explicitement l'ancien état avant de recharger
      setExecution(null);
      setLogs([]);
      setSteps([]);

      // Recharger complètement l'exécution
      await loadExecution();
      toast.success('🔄 Execution queued for retry');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to retry execution');
    }
  };

  /**
   * Approuver l'exécution
   */
  const handleApprove = () => {
    if (!runId) return;
    setApprovalComment('');
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    if (!runId) return;

    try {
      await approveExecution(runId, approvalComment || undefined);
      await loadExecution();
      toast.success('✅ Execution approved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to approve execution');
    }
  };

  /**
   * Rejeter l'exécution
   */
  const handleReject = () => {
    if (!runId) return;
    setRejectReason('');
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!runId) return;
    try {
      await rejectExecution(runId, rejectReason || 'Rejected by approver');
      setShowRejectModal(false);
      await loadExecution();
      toast.success('❌ Execution rejected');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to reject execution');
    }
  };

  /**
   * Créer une exécution Apply à partir de ce Plan
   */
  const handleCreateApply = () => {
    if (!execution) return;

    // Navigate to new execution page with plan run ID pre-filled and action set to apply
    navigate(`/executions/new?action=apply&plan_run_id=${execution.run_id}&architecture_id=${execution.architecture_id}&environment_id=${execution.environment_id}&version_id=${execution.architecture_version_id}`);
  };

  /**
   * Obtenir la classe du statut
   */
  const getStatusClass = (status: string): string => {
    const map: Record<string, string> = {
      pending: 'status-pending',
      queued: 'status-queued',
      running: 'status-running',
      succeeded: 'status-succeeded',
      failed: 'status-failed',
      cancelled: 'status-cancelled',
      pending_approval: 'status-approval',
      approved: 'status-approved'
    };
    return map[status] || 'status-default';
  };

  /**
   * Formater la durée
   */
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  /**
   * Formater la date
   */
  const formatDate = (date?: string): string => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  /**
   * Calculer le pourcentage de progression des steps
   */
  const calculateProgress = (): number => {
    if (steps.length === 0) return 0;

    const completedSteps = steps.filter(s =>
      ['succeeded', 'failed', 'skipped'].includes(s.status)
    ).length;

    return Math.round((completedSteps / steps.length) * 100);
  };

  /**
   * Formater le temps écoulé (HH:MM:SS)
   */
  const formatElapsedTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Obtenir la couleur du log selon le niveau
   */
  const getLogLevelClass = (level: string): string => {
    const map: Record<string, string> = {
      info: 'log-info',
      warning: 'log-warning',
      error: 'log-error',
      debug: 'log-debug'
    };
    return map[level] || 'log-info';
  };

  /**
   * Filtrer les logs selon le niveau et la recherche
   */
  const getFilteredLogs = (): ExecutionLog[] => {
    let filtered = logs;

    // Filter by level
    if (logLevelFilter !== 'all') {
      filtered = filtered.filter(log => log.level === logLevelFilter);
    }

    // Filter by search query
    if (logSearchQuery.trim()) {
      const query = logSearchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        log.message.toLowerCase().includes(query) ||
        log.source?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  /**
   * Télécharger les logs filtrés
   */
  const handleDownloadLogs = () => {
    const filteredLogs = getFilteredLogs();
    if (filteredLogs.length === 0) {
      alert('No logs to download');
      return;
    }

    const logsText = filteredLogs.map(log =>
      `[${new Date(log.timestamp).toISOString()}] [${log.level.toUpperCase()}] ${log.message}`
    ).join('\n');

    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `execution-${execution?.run_id}-logs.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Obtenir l'URL externe de l'exécution (Azure DevOps ou Kubernetes)
   */
  const getExternalUrl = (): string | null => {
    if (!execution?.external_job_id) return null;

    // Parse external_job_id format: "ado:12345" or "k8s:job-name" or "mock:12345"
    const [engine, jobId] = execution.external_job_id.split(':');

    if (engine === 'ado') {
      // Azure DevOps URL (à adapter selon votre configuration)
      const orgName = 'your-org'; // TODO: récupérer depuis la config
      const projectName = 'terraform-executions';
      return `https://dev.azure.com/${orgName}/${projectName}/_build/results?buildId=${jobId}`;
    } else if (engine === 'k8s') {
      // Kubernetes dashboard URL (à adapter selon votre configuration)
      const namespace = 'terraform-jobs'; // TODO: récupérer depuis la config
      return `https://kubernetes-dashboard/#!/job/${namespace}/${jobId}`;
    }

    return null;
  };

  /**
   * Exporter les logs d'une étape au format texte
   */
  const handleExportStepLogs = (step: ExecutionStep) => {
    if (!step.logs) return;

    const blob = new Blob([step.logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${execution?.run_id}-${step.step_name}-logs.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Exporter tous les logs de l'exécution au format texte
   */
  const handleExportAllLogs = () => {
    if (logs.length === 0) return;

    // Convertir les logs en texte
    const logsText = logs.map(log =>
      `[${log.timestamp || new Date().toISOString()}] [${log.level || 'INFO'}] ${log.message}`
    ).join('\n');

    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${execution?.run_id}-all-logs.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Retry une étape qui a échoué (TODO: implémenter l'API)
   */
  const handleRetryStep = async (step: ExecutionStep) => {
    if (!window.confirm(`Are you sure you want to retry the step "${step.step_display_name}"?`)) {
      return;
    }

    // TODO: Implémenter l'API pour retry une étape spécifique
    alert('Retry step feature will be implemented with the backend API');
    console.log('Retry step:', step.step_name);
  };

  if (loading) {
    return (
      <div className="execution-details-page text-gray-900">
        <div className="loading">Loading execution details...</div>
      </div>
    );
  }

  if (error || !execution) {
    return (
      <DashboardLayout>
        <div className="execution-details-page text-gray-900">
          <div className="error-state">
            ⚠️ {error || 'Execution not found'}
            <button className="btn btn-secondary" onClick={() => navigate('/executions')}>
              ← Back to Executions
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="execution-details-page text-gray-900">
      {/* Header */}
      <div className="details-header">
        <button className="btn btn-text" onClick={() => navigate('/executions')}>
          ← Back
        </button>
        <div className="header-content">
          <h1>
            {execution.action === 'plan' && '📋'}
            {execution.action === 'apply' && '🚀'}
            {execution.action === 'destroy' && '🗑️'}
            {' '}
            Execution Details
          </h1>
          <code className="run-id">{execution.run_id}</code>
        </div>
        <div className="header-actions">
          {getExternalUrl() && (
            <a
              href={getExternalUrl()!}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              🔗 View External
            </a>
          )}
          {logs.length > 0 && (
            <button className="btn btn-secondary" onClick={handleExportAllLogs}>
              💾 Export Logs
            </button>
          )}
          {execution.action === 'plan' && (execution.status === 'success' || execution.status === 'succeeded') && (
            <button className="btn btn-success" onClick={handleCreateApply}>
              🚀 Create Apply
            </button>
          )}
          {execution.status === 'pending_approval' && (
            <>
              <button className="btn btn-success" onClick={handleApprove}>
                ✓ Approuver
              </button>
              <button className="btn btn-danger" onClick={handleReject}>
                ✗ Rejeter
              </button>
            </>
          )}
          {['pending', 'queued', 'running'].includes(execution.status) && (
            <button className="btn btn-danger" onClick={handleCancel}>
              ✖ Cancel
            </button>
          )}
          {execution.status === 'failed' && (
            <button className="btn btn-primary" onClick={handleRetry}>
              🔄 Retry
            </button>
          )}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="overview-cards">
        <div className="overview-card">
          <div className="card-label">Status</div>
          <div className={`card-value ${getStatusClass(execution.status || 'pending')}`}>
            {(execution.status || 'pending').replace('_', ' ')}
          </div>
        </div>
        <div className="overview-card">
          <div className="card-label">Action</div>
          <div className="card-value">{(execution.action || 'plan').toUpperCase()}</div>
        </div>
        <div className="overview-card">
          <div className="card-label">Environment</div>
          <div className="card-value">{execution.environment_name || '-'}</div>
          <div className="card-subtitle">{execution.environment_type || '-'}</div>
        </div>
        <div className="overview-card">
          <div className="card-label">Architecture</div>
          <div className="card-value">{execution.architecture_name || '-'}</div>
          <div className="card-subtitle">v{execution.version_number || '?'}</div>
        </div>
        <div className="overview-card">
          <div className="card-label">Duration</div>
          <div className="card-value">{formatDuration(execution.duration_seconds || 0)}</div>
        </div>
        <div className="overview-card">
          <div className="card-label">Engine</div>
          <div className="card-value">
            {execution.engine_type === 'mock' && '🧪 Mock'}
            {execution.engine_type === 'azure-devops' && '☁️ Azure DevOps'}
            {execution.engine_type === 'kubernetes-job' && '⚙️ Kubernetes'}
            {execution.engine_type && !['mock', 'azure-devops', 'kubernetes-job'].includes(execution.engine_type) && execution.engine_type}
            {!execution.engine_type && '-'}
          </div>
        </div>
      </div>

      {/* Bandeau approbation en attente */}
      {execution.status === 'pending_approval' && (
        <div style={{
          background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px',
          padding: '16px 20px', marginBottom: '16px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>⏳</span>
            <div>
              <div style={{ fontWeight: '600', color: '#92400e' }}>En attente d'approbation</div>
              <div style={{ fontSize: '13px', color: '#b45309' }}>
                Cette exécution nécessite une approbation avant de démarrer.
                {(execution as any).approved_count !== undefined && (execution as any).required_count !== undefined && (
                  <span> — {(execution as any).approved_count}/{(execution as any).required_count} approbation(s) reçue(s)</span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-success" onClick={handleApprove}>✓ Approuver</button>
            <button className="btn btn-danger" onClick={handleReject}>✗ Rejeter</button>
          </div>
        </div>
      )}

      {/* Progress Bar Section */}
      {steps.length > 0 && (
        <div className="progress-section">
          <div className="progress-header">
            <div className="progress-info">
              <span className="progress-label">Overall Progress</span>
              <span className="progress-percentage">{calculateProgress()}%</span>
            </div>
            <div className="progress-time">
              <span className="time-icon">⏱️</span>
              <span className="elapsed-time">{formatElapsedTime(elapsedTime)}</span>
              {execution.duration_seconds && (
                <span className="total-duration"> / {formatDuration(execution.duration_seconds)}</span>
              )}
            </div>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${calculateProgress()}%`,
                backgroundColor:
                  execution.status === 'failed'
                    ? '#ef4444'
                    : execution.status === 'running'
                    ? '#3b82f6'
                    : execution.status === 'success' || execution.status === 'succeeded'
                    ? '#10b981'
                    : '#94a3b8'
              }}
            />
          </div>
          <div className="progress-stats">
            <span>{steps.filter(s => s.status === 'succeeded').length} completed</span>
            <span>{steps.filter(s => s.status === 'running').length} running</span>
            <span>{steps.filter(s => s.status === 'failed').length} failed</span>
            <span>{steps.filter(s => s.status === 'pending').length} pending</span>
          </div>
        </div>
      )}

      {/* Timeline Step-by-Step */}
      {steps.length > 0 && (
        <div className="timeline-section">
          <h2>📊 Execution Timeline</h2>
          <div className="pipeline-timeline">
            {steps.map((step) => (
              <StepCard
                key={step.id}
                step={step}
                isSelected={selectedStep?.id === step.id}
                onClick={() => {
                  userSelectedStepRef.current = true;
                  setSelectedStep(step);
                }}
                avgDuration={stepStatistics[step.step_name]?.avg_duration}
              />
            ))}
          </div>
        </div>
      )}

      {/* Step Logs Panel */}
      {selectedStep && selectedStep.logs && (
        <div className="step-logs-panel">
          <div className="step-logs-header">
            <div className="step-logs-title">
              📝 Logs: {selectedStep.step_display_name}
            </div>
            <div className="step-logs-actions">
              {selectedStep.duration_seconds && (
                <div className="step-card-duration">
                  Duration: {selectedStep.duration_seconds}s
                </div>
              )}
              <button
                className="btn btn-icon"
                onClick={() => handleExportStepLogs(selectedStep)}
                title="Export step logs"
              >
                💾
              </button>
              {selectedStep.status === 'failed' && (
                <button
                  className="btn btn-icon"
                  onClick={() => handleRetryStep(selectedStep)}
                  title="Retry this step"
                >
                  🔄
                </button>
              )}
            </div>
          </div>
          <pre className="step-logs-content">
            {selectedStep.logs}
          </pre>
        </div>
      )}

      {selectedStep && !selectedStep.logs && selectedStep.status !== 'pending' && (
        <div className="step-logs-panel">
          <div className="step-logs-header">
            <div className="step-logs-title">
              📝 Logs: {selectedStep.step_display_name}
            </div>
          </div>
          <div className="step-logs-empty">
            No logs available for this step yet...
          </div>
        </div>
      )}

      {/* Timeline & Metadata */}
      <div className="execution-metadata-section">
        <div className="timeline">
          <div className="timeline-item">
            <div className="timeline-label">Created</div>
            <div className="timeline-value">{formatDate(execution.created_at)}</div>
          </div>
          {execution.started_at && (
            <div className="timeline-item">
              <div className="timeline-label">Started</div>
              <div className="timeline-value">{formatDate(execution.started_at)}</div>
            </div>
          )}
          {execution.completed_at && (
            <div className="timeline-item">
              <div className="timeline-label">Completed</div>
              <div className="timeline-value">{formatDate(execution.completed_at)}</div>
            </div>
          )}
        </div>

        {/* Enriched Metadata */}
        <div className="execution-enriched-metadata">
          <h3>📋 Execution Metadata</h3>
          <div className="metadata-grid">
            {/* Triggered By - Always show with fallback */}
            <div className="metadata-item">
              <div className="metadata-label">👤 Triggered By</div>
              <div className="metadata-value">
                {execution.initiated_by_name || 'Unknown User'}
              </div>
            </div>

            {/* Run ID - Always show */}
            <div className="metadata-item">
              <div className="metadata-label">🎯 Run ID</div>
              <div className="metadata-value metadata-code">{execution.run_id}</div>
            </div>

            {/* Terraform Version - Always show with fallback */}
            <div className="metadata-item">
              <div className="metadata-label">🔧 Terraform Version</div>
              <div className="metadata-value">{execution.terraform_version || '1.6.0'}</div>
            </div>

            {/* Based on Plan - Show only for apply/destroy with plan_run_id */}
            {execution.plan_run_id && (execution.action === 'apply' || execution.action === 'destroy') && (
              <div className="metadata-item">
                <div className="metadata-label">📋 Based on Plan</div>
                <div className="metadata-value metadata-code">
                  <a href={`/executions/${execution.plan_run_id}`} className="metadata-link">
                    {execution.plan_run_id.substring(0, 8)}...
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resources Summary */}
      {((execution.resources_created || 0) > 0 || (execution.resources_updated || 0) > 0 || (execution.resources_deleted || 0) > 0) && (
        <div className="resources-summary">
          <h3>📊 Resources Changed</h3>
          <div className="resources-grid">
            {(execution.resources_created || 0) > 0 && (
              <div className="resource-item resource-created">
                <div className="resource-icon">➕</div>
                <div className="resource-count">{execution.resources_created}</div>
                <div className="resource-label">Created</div>
              </div>
            )}
            {(execution.resources_updated || 0) > 0 && (
              <div className="resource-item resource-updated">
                <div className="resource-icon">🔄</div>
                <div className="resource-count">{execution.resources_updated}</div>
                <div className="resource-label">Updated</div>
              </div>
            )}
            {(execution.resources_deleted || 0) > 0 && (
              <div className="resource-item resource-deleted">
                <div className="resource-icon">➖</div>
                <div className="resource-count">{execution.resources_deleted}</div>
                <div className="resource-label">Deleted</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Terraform Output */}
      {execution.terraform_output && (
        <div className="terraform-output">
          <CodeBlock
            code={JSON.stringify(execution.terraform_output, null, 2)}
            language="json"
            title="🎯 Terraform Output"
            maxHeight="400px"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📝 Logs {logs.length > 0 && `(${logs.length})`}
          </button>
          {execution.action === 'plan' && (
            <button
              className={`tab ${activeTab === 'plan' ? 'active' : ''}`}
              onClick={() => setActiveTab('plan')}
            >
              📋 Plan Output
            </button>
          )}
          {execution.action === 'apply' && (
            <button
              className={`tab ${activeTab === 'state' ? 'active' : ''}`}
              onClick={() => setActiveTab('state')}
            >
              💾 Terraform State
            </button>
          )}
          <button
            className={`tab ${activeTab === 'artifacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('artifacts')}
          >
            📦 Artifacts ({artifacts.length})
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="logs-container">
              <div className="logs-controls">
                <div className="logs-controls-left">
                  {/* Level Filter */}
                  <select
                    className="log-filter-select"
                    value={logLevelFilter}
                    onChange={(e) => setLogLevelFilter(e.target.value)}
                  >
                    <option value="all">All Levels</option>
                    <option value="info">ℹ️ Info</option>
                    <option value="warning">⚠️ Warning</option>
                    <option value="error">❌ Error</option>
                    <option value="debug">🐛 Debug</option>
                  </select>

                  {/* Search Input */}
                  <input
                    type="text"
                    className="log-search-input"
                    placeholder="🔍 Search logs..."
                    value={logSearchQuery}
                    onChange={(e) => setLogSearchQuery(e.target.value)}
                  />

                  {/* Auto-scroll Checkbox */}
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={autoScroll}
                      onChange={(e) => setAutoScroll(e.target.checked)}
                    />
                    Auto-scroll
                  </label>
                </div>

                <div className="logs-controls-right">
                  {/* Download Button */}
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleDownloadLogs}
                    disabled={logs.length === 0}
                    title="Download logs"
                  >
                    ⬇️ Download
                  </button>

                  {/* Live Indicator */}
                  {['pending', 'queued', 'running'].includes(execution.status) && (
                    <div className="live-indicator">
                      <span className="pulse-dot"></span>
                      Live
                    </div>
                  )}

                  {/* Log Count */}
                  <span className="log-count">
                    {getFilteredLogs().length} / {logs.length} logs
                  </span>
                </div>
              </div>
              <div className="logs-view">
                {(() => {
                  const filteredLogs = getFilteredLogs();
                  if (filteredLogs.length === 0) {
                    return (
                      <div className="empty-logs">
                        {logs.length === 0
                          ? (['pending', 'queued'].includes(execution.status)
                              ? '⏳ Waiting for execution to start...'
                              : 'No logs available')
                          : '🔍 No logs match your filters'}
                      </div>
                    );
                  }
                  return filteredLogs.map((log, index) => (
                    <div key={index} className={`log-line ${getLogLevelClass(log.level)}`}>
                      <span className="log-timestamp">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      <span className="log-level">[{log.level.toUpperCase()}]</span>
                      <span className="log-message">{log.message}</span>
                    </div>
                  ));
                })()}
                <div ref={logsEndRef} />
              </div>
            </div>
          )}

          {/* Plan Tab */}
          {activeTab === 'plan' && (
            <div className="content-view plan-output-view">
              {planOutput ? (
                <div className="plan-output-container">
                  {/* Plan Summary */}
                  {execution.terraform_plan_summary && (
                    <div className="plan-summary">
                      <h3>📊 Plan Summary</h3>
                      <div className="plan-summary-grid">
                        <div className="plan-summary-item plan-create">
                          <div className="summary-icon">➕</div>
                          <div className="summary-label">To Create</div>
                          <div className="summary-value">{execution.resources_created || 0}</div>
                        </div>
                        <div className="plan-summary-item plan-update">
                          <div className="summary-icon">🔄</div>
                          <div className="summary-label">To Update</div>
                          <div className="summary-value">{execution.resources_updated || 0}</div>
                        </div>
                        <div className="plan-summary-item plan-delete">
                          <div className="summary-icon">🗑️</div>
                          <div className="summary-label">To Delete</div>
                          <div className="summary-value">{execution.resources_deleted || 0}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Plan Details */}
                  <div className="plan-details">
                    <CodeBlock
                      code={JSON.stringify(planOutput, null, 2)}
                      language="json"
                      title="📋 Plan Details"
                      maxHeight="600px"
                    />
                  </div>

                  {/* Download Button */}
                  <div className="plan-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        const blob = new Blob([JSON.stringify(planOutput, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `plan-${execution.run_id}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      ⬇️ Download Plan
                    </button>
                  </div>
                </div>
              ) : (
                <div className="empty-content">
                  {execution.status === 'succeeded'
                    ? 'Plan output not available'
                    : '⏳ Plan output will appear when execution completes'}
                </div>
              )}
            </div>
          )}

          {/* State Tab */}
          {activeTab === 'state' && (
            <div className="content-view">
              {terraformState ? (
                <CodeBlock
                  code={JSON.stringify(terraformState, null, 2)}
                  language="json"
                  title="💾 Terraform State"
                  maxHeight="600px"
                />
              ) : (
                <div className="empty-content">
                  {execution.status === 'succeeded'
                    ? 'Terraform state not available'
                    : '⏳ State will appear when apply completes'}
                </div>
              )}
            </div>
          )}

          {/* Artifacts Tab */}
          {activeTab === 'artifacts' && (
            <div className="artifacts-list">
              {artifacts.length === 0 ? (
                <div className="empty-content">No artifacts available</div>
              ) : (
                artifacts.map((artifact) => (
                  <div key={artifact.id} className="artifact-item">
                    <div className="artifact-icon">
                      {artifact.artifact_type === 'bundle' && '📦'}
                      {artifact.artifact_type === 'plan' && '📋'}
                      {artifact.artifact_type === 'state' && '💾'}
                      {artifact.artifact_type === 'logs' && '📝'}
                      {artifact.artifact_type === 'manifest' && '📄'}
                    </div>
                    <div className="artifact-info">
                      <div className="artifact-type">{artifact.artifact_type}</div>
                      <div className="artifact-meta">
                        Size: {(artifact.size_bytes / 1024).toFixed(2)} KB
                        {' • '}
                        Created: {formatDate(artifact.created_at)}
                        {artifact.expires_at && (
                          <>
                            {' • '}
                            Expires: {formatDate(artifact.expires_at)}
                          </>
                        )}
                      </div>
                    </div>
                    {!artifact.is_deleted && (
                      <a
                        href={artifact.storage_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-icon"
                        title="Download"
                      >
                        ⬇️
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {execution.error_message && execution.error_message !== 'null' && (
        <div className="error-message">
          <strong>Error:</strong> {execution.error_message}
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
        title="Cancel Execution"
        message="Are you sure you want to cancel this execution? This action cannot be undone."
        confirmText="Yes, Cancel"
        cancelText="No, Keep Running"
        type="danger"
      />

      <ConfirmationModal
        isOpen={showRetryModal}
        onClose={() => setShowRetryModal(false)}
        onConfirm={confirmRetry}
        title="Retry Execution"
        message="Are you sure you want to retry this execution? A new execution will be queued with the same parameters."
        confirmText="Yes, Retry"
        cancelText="No, Go Back"
        type="warning"
      />

      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={confirmApprove}
        title="Approve Execution"
        message="Are you sure you want to approve this execution? The execution will proceed to the next stage."
        confirmText="Yes, Approve"
        cancelText="No, Go Back"
        type="info"
      >
        <div style={{ marginTop: '12px' }}>
          <label htmlFor="approval-comment" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            Comment (optional):
          </label>
          <textarea
            id="approval-comment"
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            placeholder="Add a comment about this approval..."
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={confirmReject}
        title="Rejeter l'exécution"
        message="Cette exécution sera rejetée et ne sera pas lancée."
        confirmText="Confirmer le rejet"
        cancelText="Annuler"
        type="danger"
      >
        <div style={{ marginTop: '12px' }}>
          <label htmlFor="reject-reason" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            Motif du rejet (obligatoire) :
          </label>
          <textarea
            id="reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Expliquez pourquoi cette exécution est rejetée..."
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>
      </ConfirmationModal>
    </div>
    </DashboardLayout>
  );
};

export default ExecutionDetailsPage;
